import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useCheckoutForm from '../global/useCheckoutForm';
import {
  getPayment,
  invalidateGroupedPayments,
  invalidatePaymentPaymentsGroup,
} from '../../services/payments';
import useCalendarSelectedAppointment from '../appointments/useCalendarSelectedAppointment';
import { checkPaymentType, isCardPaymentMethod } from '../../helpers/payments';
import {
  getAppointmentTransactions,
  invalidateAppointment,
  invalidateDepositTransferAppointments,
} from '../../services/appointments';
import { PaymentsGroup } from '../../types/payments';
import { Appointment } from '../../interfaces';
import type useSplitPayments from './useSplitPayments';
import {
  invalidateCustomerGiftCardBalances,
  invalidateGiftCardsPage,
} from '../../services/gift-cards';
import useCheckoutState from '../checkouts/useCheckoutState';

interface Params {
  enabled?: boolean;
  appointmentPayments?: Awaited<ReturnType<typeof getAppointmentTransactions>>;
  paymentsGroup?: PaymentsGroup;
  appointment?: Appointment;
  splitPayment?: {
    pendingTerminalPayment: ReturnType<
      typeof useSplitPayments
    >['pendingTerminalPayment'];
    onCheckoutCapturable: ReturnType<
      typeof useSplitPayments
    >['onCheckoutCapturable'];
    onCheckoutFailed: ReturnType<typeof useSplitPayments>['onCheckoutFailed'];
  };
}

function useCheckPendingPayment({
  enabled = true,
  appointmentPayments,
  appointment: passedAppointment,
  paymentsGroup: passedPaymentsGroup,
  splitPayment,
}: Params = {}) {
  const {
    mostRecentPaymentId,
    terminalCheckoutModal,
    setTerminalCheckoutModalToFailed,
    setTerminalCheckoutModalToSuccess,
    resetTerminalCheckoutModalState,
    setMostRecentPaymentId,
  } = useCheckoutForm();
  const { selectedPaymentsGroup } = useCheckoutState();
  const {
    setSelectedAppointmentUpdated,
    openedAppointment: currentOpenedAppointment,
  } = useCalendarSelectedAppointment();
  const paymentsGroup = passedPaymentsGroup ?? selectedPaymentsGroup;
  const openedAppointment = passedAppointment ?? currentOpenedAppointment;

  const pendingTerminalPaymentId = useMemo(
    () =>
      mostRecentPaymentId && terminalCheckoutModal.status === 'loading'
        ? mostRecentPaymentId
        : null,
    [mostRecentPaymentId, terminalCheckoutModal.status],
  );
  const pendingPaymentIdFromPaymentsGroup = useMemo(
    () =>
      paymentsGroup?.appointment?.checkoutSessionActive ||
      paymentsGroup?.order?.checkoutSessionActive
        ? paymentsGroup.payments.find(
            p =>
              isCardPaymentMethod(p.method) &&
              (p.status === 'PENDING' || p.status === 'FAILED'),
          )?.id || null
        : null,
    [paymentsGroup],
  );
  const pendingPaymentIdFromOpenedAppointment = useMemo(
    () =>
      openedAppointment?.checkoutSessionActive && appointmentPayments?.length
        ? appointmentPayments.find(
            p =>
              isCardPaymentMethod(p.method) &&
              (p.status === 'PENDING' || p.status === 'FAILED') &&
              checkPaymentType(p, 'FULL'),
          )?.id || null
        : null,
    [openedAppointment?.checkoutSessionActive, appointmentPayments],
  );
  const pendingPaymentIdFromSplitPayments = useMemo(
    () => splitPayment?.pendingTerminalPayment?.paymentId,
    [splitPayment?.pendingTerminalPayment?.paymentId],
  );
  const paymentId = useMemo(
    () =>
      pendingTerminalPaymentId ||
      pendingPaymentIdFromPaymentsGroup ||
      pendingPaymentIdFromOpenedAppointment ||
      pendingPaymentIdFromSplitPayments,
    [
      pendingTerminalPaymentId,
      pendingPaymentIdFromPaymentsGroup,
      pendingPaymentIdFromOpenedAppointment,
      pendingPaymentIdFromSplitPayments,
    ],
  );
  const isEnabled = enabled && !!paymentId;

  useQuery({
    queryKey: ['pending-payment', paymentId],
    queryFn: async ({ signal }) => {
      const payment = await getPayment({
        paymentId: paymentId!,
        signal,
      });
      const isStillPending =
        !!payment.order?.checkoutSessionActive ||
        !!payment.appointment?.checkoutSessionActive;
      if (isStillPending) return null;

      const updateAppt = () => {
        if (
          openedAppointment &&
          payment.appointment &&
          payment.appointment.appointmentId === openedAppointment.appointmentId
        ) {
          setSelectedAppointmentUpdated(true);
        }
        if (payment.appointment) {
          invalidateAppointment(payment.appointment.appointmentId);
        }
      };

      if (
        payment.status === 'SUCCESS' ||
        (payment.status === 'ARCHIVED' &&
          payment.splitPaymentsGroup?.payments.some(
            p => p.status === 'SUCCESS',
          ))
      ) {
        setMostRecentPaymentId('');
        if (terminalCheckoutModal.status === 'loading') {
          setTerminalCheckoutModalToSuccess({
            paymentId: payment.id,
            isGuest:
              !payment.customerId &&
              !payment.appointment?.customerId &&
              !payment.order?.customerId,
          });
        } else {
          resetTerminalCheckoutModalState();
        }
        updateAppt();
        invalidateDepositTransferAppointments();
        invalidateGroupedPayments();
        invalidateCustomerGiftCardBalances();
        invalidateGiftCardsPage();
        payment.splitPaymentsGroup?.payments?.forEach(p =>
          invalidatePaymentPaymentsGroup(p.id),
        );
      }

      if (payment.status === 'FAILED' || payment.error) {
        const errorMessage = payment.error
          ? `Error message: ${payment.error}`
          : 'This payment has failed.';
        if (terminalCheckoutModal.status === 'loading') {
          setTerminalCheckoutModalToFailed(errorMessage);
        }
        if (payment.status === 'UNCAPTURED') {
          splitPayment?.onCheckoutFailed({
            paymentId: payment.id,
            error: errorMessage,
            updateModal: false,
          });
        }
        invalidateGroupedPayments();
        setMostRecentPaymentId('');
        updateAppt();
      }

      if (payment.status === 'UNCAPTURED' && payment.isCapturable) {
        splitPayment?.onCheckoutCapturable(payment.id);
      }

      return null;
    },
    enabled: isEnabled,
    refetchInterval: 7500,
    staleTime: 0,
    gcTime: 0,
  });
}

export default useCheckPendingPayment;
